import React, { useEffect, useMemo, useState } from 'react';
import { Check, FileText, FolderOpen, HardDriveUpload, Server } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { Progress } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX } from '../../../lib/zLayers';
import { formatFileSize } from '../../../components/attachments/fileIconUtils';

export type FolderImportPhase = 'reading' | 'uploading' | 'processing' | 'completed';

export interface FolderImportProgressState {
  phase: FolderImportPhase;
  folderName: string;
  scopeLabel?: string;
  fileCount: number;
  filesRead?: number;
  latestPath?: string;
  /** 1-based index while uploading a specific file. */
  currentFileIndex?: number;
  bytesUploaded?: number;
  bytesTotal?: number;
  uploadPercent?: number;
  /** 0-based when batching. */
  batchIndex?: number;
  batchCount?: number;
  filesCompleted?: number;
  /** Overall 0–100 across all batches when available. */
  overallPercent?: number;
  /** Shown on the completed screen. */
  completionSummary?: {
    templatesCreated: number;
    documentsCreated: number;
    foldersCreated: number;
    skipped: number;
  };
}

interface TemplateFolderImportProgressModalProps {
  progress: FolderImportProgressState | null;
  onDismiss?: () => void;
}

type StepStatus = 'pending' | 'active' | 'done';

const PHASE_ORDER: FolderImportPhase[] = ['reading', 'uploading', 'processing'];

const STEPS: Array<{
  key: FolderImportPhase;
  label: string;
  shortLabel: string;
  description: string;
  Icon: typeof FolderOpen;
}> = [
  {
    key: 'reading',
    label: 'Prepare',
    shortLabel: '1',
    description: 'Scan files locally',
    Icon: FolderOpen,
  },
  {
    key: 'uploading',
    label: 'Upload',
    shortLabel: '2',
    description: 'Send to server temp',
    Icon: HardDriveUpload,
  },
  {
    key: 'processing',
    label: 'Import',
    shortLabel: '3',
    description: 'Add to library',
    Icon: Server,
  },
];

function stepStatus(current: FolderImportPhase, step: FolderImportPhase): StepStatus {
  if (current === 'completed') return 'done';
  const currentIdx = PHASE_ORDER.indexOf(current);
  const stepIdx = PHASE_ORDER.indexOf(step);
  if (stepIdx < currentIdx) return 'done';
  if (stepIdx === currentIdx) return 'active';
  return 'pending';
}

function fileBaseName(path: string): string {
  const normalized = path.replace(/\\/g, '/');
  const slash = normalized.lastIndexOf('/');
  return slash >= 0 ? normalized.slice(slash + 1) : normalized;
}

function fileParentPath(path: string): string | null {
  const normalized = path.replace(/\\/g, '/');
  const slash = normalized.lastIndexOf('/');
  if (slash <= 0) return null;
  return normalized.slice(0, slash);
}

const TemplateFolderImportProgressModal: React.FC<TemplateFolderImportProgressModalProps> = ({
  progress,
  onDismiss,
}) => {
  const { colors } = useTheme();
  const [processingPct, setProcessingPct] = useState(82);

  useEffect(() => {
    if (progress?.phase !== 'processing') return;
    if (progress.overallPercent != null) return;
    setProcessingPct(82);
    const id = window.setInterval(() => {
      setProcessingPct((prev) => {
        if (prev >= 99) return 99;
        if (prev >= 96) return Math.min(99, prev + 0.08);
        if (prev >= 90) return prev + 0.2;
        return prev + 0.45;
      });
    }, 250);
    return () => window.clearInterval(id);
  }, [progress?.phase, progress?.overallPercent]);

  const view = useMemo(() => {
    if (!progress) {
      return {
        overallPercent: 0,
        phaseTitle: '',
        phaseHint: '',
        fileProgressLabel: '',
        fileProgressValue: 0,
        fileProgressMax: 1,
        bytesLabel: null as string | null,
        batchLabel: null as string | null,
        currentFileName: null as string | null,
        currentFileFolder: null as string | null,
        showCurrentFile: false,
      };
    }

    const {
      phase,
      fileCount,
      filesRead = 0,
      bytesUploaded = 0,
      bytesTotal = 0,
      uploadPercent = 0,
      batchIndex,
      batchCount = 1,
      filesCompleted = 0,
      overallPercent,
      currentFileIndex,
      latestPath,
    } = progress;

    const batchLabel =
      batchCount > 1 && batchIndex != null
        ? `Batch ${batchIndex + 1} of ${batchCount}`
        : null;

    const bytesLabel =
      bytesTotal > 0
        ? `${formatFileSize(bytesUploaded)} / ${formatFileSize(bytesTotal)}`
        : null;

    if (phase === 'completed') {
      return {
        overallPercent: 100,
        phaseTitle: 'Import completed',
        phaseHint: 'Your folder has been added to the library.',
        fileProgressLabel:
          fileCount > 0
            ? `${fileCount} of ${fileCount} files imported`
            : 'Import finished',
        fileProgressValue: fileCount,
        fileProgressMax: Math.max(fileCount, 1),
        bytesLabel: bytesTotal > 0 ? formatFileSize(bytesTotal) : null,
        batchLabel: null,
        currentFileName: null,
        currentFileFolder: null,
        showCurrentFile: false,
      };
    }

    if (phase === 'reading') {
      const pct = filesRead > 0 ? Math.min(14, 4 + filesRead * 0.4) : 3;
      return {
        overallPercent: pct,
        phaseTitle: 'Preparing files',
        phaseHint: 'Reading the folder on your computer before upload starts.',
        fileProgressLabel:
          filesRead > 0
            ? `${filesRead} file${filesRead === 1 ? '' : 's'} scanned`
            : 'Scanning folder…',
        fileProgressValue: Math.max(filesRead, 0),
        fileProgressMax: Math.max(fileCount, filesRead, 1),
        bytesLabel: null,
        batchLabel: null,
        currentFileName: latestPath ? fileBaseName(latestPath) : null,
        currentFileFolder: latestPath ? fileParentPath(latestPath) : null,
        showCurrentFile: Boolean(latestPath),
      };
    }

    if (phase === 'uploading') {
      const pct =
        overallPercent != null ? overallPercent : 15 + uploadPercent * 0.65;
      const currentIdx = currentFileIndex ?? Math.min(filesCompleted + 1, fileCount);
      return {
        overallPercent: pct,
        phaseTitle: 'Uploading to server',
        phaseHint: 'Files are stored temporarily, then imported into the library.',
        fileProgressLabel:
          fileCount > 0
            ? `File ${Math.min(currentIdx, fileCount)} of ${fileCount}`
            : 'Uploading…',
        fileProgressValue: Math.min(currentIdx, fileCount),
        fileProgressMax: Math.max(fileCount, 1),
        bytesLabel,
        batchLabel,
        currentFileName: latestPath ? fileBaseName(latestPath) : null,
        currentFileFolder: latestPath ? fileParentPath(latestPath) : null,
        showCurrentFile: true,
      };
    }

    const processingPercent =
      overallPercent != null ? Math.min(99, overallPercent) : processingPct;
    const done = Math.min(filesCompleted, fileCount);
    return {
      overallPercent: processingPercent,
      phaseTitle:
        processingPercent >= 99 ? 'Finishing import…' : 'Importing into library',
      phaseHint: 'Creating folders, templates, and documents on the server.',
      fileProgressLabel:
        fileCount > 0
          ? `${done} of ${fileCount} files processed`
          : 'Processing on server…',
      fileProgressValue: done,
      fileProgressMax: Math.max(fileCount, 1),
      bytesLabel: bytesTotal > 0 ? formatFileSize(bytesTotal) : null,
      batchLabel: null,
      currentFileName: null,
      currentFileFolder: null,
      showCurrentFile: false,
    };
  }, [progress, processingPct]);

  if (!progress) return null;

  const isCompleted = progress.phase === 'completed';
  const fileBarPct =
    view.fileProgressMax > 0
      ? Math.min(100, Math.round((view.fileProgressValue / view.fileProgressMax) * 100))
      : 0;

  const summary = progress.completionSummary;
  const summaryParts = summary
    ? [
        summary.templatesCreated > 0
          ? `${summary.templatesCreated} template${summary.templatesCreated === 1 ? '' : 's'}`
          : null,
        summary.documentsCreated > 0
          ? `${summary.documentsCreated} document${summary.documentsCreated === 1 ? '' : 's'}`
          : null,
        summary.foldersCreated > 0
          ? `${summary.foldersCreated} folder${summary.foldersCreated === 1 ? '' : 's'}`
          : null,
        summary.skipped > 0 ? `${summary.skipped} skipped` : null,
      ].filter(Boolean)
    : [];

  return (
    <Modal
      isOpen
      closeOnEscape={isCompleted}
      onClose={isCompleted && onDismiss ? onDismiss : () => {}}
      size="lg"
      zIndex={Z_INDEX.modalHigh}
    >
      <ModalHeader>
        <ModalTitle>{isCompleted ? 'Import complete' : 'Importing library folder'}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-6">
          {isCompleted ? (
            <div className="flex flex-col items-center text-center px-2 py-2">
              <div
                className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/80"
                aria-hidden
              >
                <Check className="h-9 w-9 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
              </div>
              <p className={cn('mt-4 text-lg font-semibold', colors.text)}>Import completed</p>
              <p className={cn('mt-1 text-sm', colors.textMuted)}>
                <span className="font-medium">{progress.folderName}</span>
                {progress.scopeLabel ? ` → ${progress.scopeLabel}` : ''} is ready in the library.
              </p>
              {summaryParts.length > 0 && (
                <p className={cn('mt-3 text-sm', colors.textMuted)}>{summaryParts.join(' · ')}</p>
              )}
              <div className="mt-4 w-full max-w-sm">
                <Progress value={100} max={100} size="lg" variant="success" />
                <p className={cn('mt-2 text-xs tabular-nums', colors.textMuted)}>100%</p>
              </div>
              {onDismiss && (
                <button
                  type="button"
                  onClick={onDismiss}
                  className={cn(
                    'mt-6 inline-flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium',
                    'bg-emerald-600 text-white hover:bg-emerald-700 transition-colors',
                  )}
                >
                  Done
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Destination */}
              <div className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
                <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                  Importing into
                </p>
                <p className={cn('mt-1 text-base font-semibold truncate', colors.text)} title={progress.folderName}>
                  {progress.folderName}
                </p>
                {progress.scopeLabel && (
                  <p className={cn('text-xs mt-0.5', colors.textMuted)}>{progress.scopeLabel}</p>
                )}
              </div>

              {/* Stage stepper */}
              <ol className="grid grid-cols-3 gap-2">
                {STEPS.map((step, index) => {
                  const status = stepStatus(progress.phase, step.key);
                  const Icon = step.Icon;
                  return (
                    <li key={step.key} className="min-w-0">
                      <div
                        className={cn(
                          'relative h-full rounded-xl border px-3 py-3 transition-colors',
                          colors.border,
                          status === 'active' && 'border-indigo-500/60 bg-indigo-50/80 dark:bg-indigo-950/40',
                          status === 'done' && 'border-emerald-500/40 bg-emerald-50/70 dark:bg-emerald-950/30',
                          status === 'pending' && colors.surfaceHover,
                        )}
                      >
                        <div className="flex items-start gap-2.5">
                          <span
                            className={cn(
                              'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                              status === 'done' && 'bg-emerald-500 text-white',
                              status === 'active' && 'bg-indigo-600 text-white',
                              status === 'pending' &&
                                cn('bg-white dark:bg-gray-800 border', colors.border, colors.textMuted),
                            )}
                            aria-hidden
                          >
                            {status === 'done' ? (
                              <Check className="h-4 w-4" strokeWidth={2.5} />
                            ) : status === 'active' ? (
                              <Icon className="h-4 w-4" />
                            ) : (
                              step.shortLabel || String(index + 1)
                            )}
                          </span>
                          <div className="min-w-0 pt-0.5">
                            <p
                              className={cn(
                                'text-sm font-semibold leading-tight',
                                status === 'pending' ? colors.textMuted : colors.text,
                              )}
                            >
                              {step.label}
                            </p>
                            <p className={cn('mt-0.5 text-[11px] leading-snug', colors.textMuted)}>
                              {step.description}
                            </p>
                            {status === 'active' && (
                              <p className="mt-1.5 text-[11px] font-medium text-indigo-600 dark:text-indigo-300">
                                In progress
                              </p>
                            )}
                            {status === 'done' && (
                              <p className="mt-1.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                                Done
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>

              {/* Overall + file progress */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0">
                      <p className={cn('text-sm font-semibold', colors.text)}>{view.phaseTitle}</p>
                      <p className={cn('text-xs mt-0.5', colors.textMuted)}>{view.phaseHint}</p>
                    </div>
                    <span className={cn('text-2xl font-semibold tabular-nums leading-none', colors.text)}>
                      {Math.round(view.overallPercent)}%
                    </span>
                  </div>
                  <Progress value={view.overallPercent} max={100} size="lg" />
                </div>

                <div className={cn('rounded-xl border px-4 py-3 space-y-3', colors.border)}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className={cn('text-sm font-medium', colors.text)}>{view.fileProgressLabel}</p>
                    <div
                      className={cn(
                        'flex flex-wrap items-center gap-x-3 gap-y-1 text-xs tabular-nums',
                        colors.textMuted,
                      )}
                    >
                      {view.batchLabel && <span>{view.batchLabel}</span>}
                      {view.bytesLabel && <span>{view.bytesLabel}</span>}
                      <span>{fileBarPct}%</span>
                    </div>
                  </div>
                  <Progress value={fileBarPct} max={100} size="default" />

                  {view.showCurrentFile && (
                    <div
                      className={cn(
                        'flex items-start gap-3 rounded-lg border px-3 py-2.5',
                        colors.border,
                        colors.surfaceHover,
                      )}
                    >
                      <FileText
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500"
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                          {progress.phase === 'reading' ? 'Scanning' : 'Current file'}
                        </p>
                        <p
                          className={cn('mt-0.5 text-sm font-medium truncate', colors.text)}
                          title={progress.latestPath}
                        >
                          {view.currentFileName ?? 'Preparing next file…'}
                        </p>
                        {view.currentFileFolder && (
                          <p
                            className={cn('mt-0.5 text-xs truncate font-mono', colors.textMuted)}
                            title={view.currentFileFolder}
                          >
                            {view.currentFileFolder}
                          </p>
                        )}
                      </div>
                      {progress.currentFileIndex != null && progress.fileCount > 0 && (
                        <span
                          className={cn(
                            'flex-shrink-0 rounded-md px-2 py-1 text-xs font-semibold tabular-nums',
                            'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200',
                          )}
                        >
                          {progress.currentFileIndex}/{progress.fileCount}
                        </span>
                      )}
                    </div>
                  )}

                  {progress.phase === 'processing' && (
                    <p className={cn('text-xs', colors.textMuted)}>
                      You can leave this dialog open — import continues on the server until it finishes.
                    </p>
                  )}
                </div>
              </div>

              <p className={cn('text-xs leading-relaxed', colors.textMuted)}>
                Keep this tab open. Large folders upload in batches (~20 files / 40 MB). Files over 50 MB
                are rejected before upload.
              </p>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default TemplateFolderImportProgressModal;
